import React, { useState, useEffect } from 'react';
import { 
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Typography,
    TextField,
    Box,
    Button,
    LinearProgress,
    InputAdornment,
    Chip,
    Stack
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { reportService } from '../../../services/api';
import ResponsiveTable from '../../common/ResponsiveTable';
import { tokens } from '../../../theme';

const LearnerProgressReport = () => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);    
    
    const theme = useTheme();
    const [filters, setFilters] = useState({
        search: '',
        // Future filters can be added here        
    });
    const [searchTimeout, setSearchTimeout] = useState(null);

    const columns = [
        { id: 'learnername', label: 'Learner Name', minWidth: 170 },
        { id: 'coursename', label: 'Course Name', minWidth: 170 },
        { id: 'completedpercent', label: 'Completed %', minWidth: 120 },
        { 
            id: 'moduleCompleted', 
            label: 'Modules Completed', 
            minWidth: 120,
            format: (row) => `${row.completedmodules} / ${row.totalmodules}`
        },
        { id: 'coursestarted', label: 'Course Started', minWidth: 120 },
        { id: 'coursecompleted', label: 'Course Completed', minWidth: 120 },
        { id: 'lastActive', label: 'Last Active', minWidth: 120 },
        { id: 'skillCoverage', label: 'Skill Coverage', minWidth: 250, format: (row) => {if (row.skillCoverage === 'No skills mapped') return row.skillCoverage;
            return (
                /*
                <div style={{ whiteSpace: 'pre-line' }}>
                    {row.skillCoverage}
                </div>
                */

                <Stack direction="column" spacing={1}> 
                    {row.skillCoverage.split('\n').map((skill, index) => (
                        <Chip 
                            key={index}
                            label={skill}
                            title={skill}                       
                            size="small"
                            sx={{
                                maxWidth: 150,
                                width: 'fit-content',
                                '& .MuiChip-label': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                                },
                            }}
                        />
                    ))}
                </Stack>
            );
        }}
    ];

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await reportService.getLearnerProgress(page, rowsPerPage, filters.search);
            setData(response.data.data);
            setTotal(response.data.total);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async () => {
        try {
            // Only include non-empty filters
            const activeFilters = Object.entries(filters)
                .reduce((acc, [key, value]) => {
                    if (value) acc[key] = value;
                    return acc;
                }, {});

            const queryParams = new URLSearchParams(activeFilters).toString();

            const response = await fetch(`/api/reports/learner-progress/export?${queryParams}`, {
                method: 'GET',
                headers: {
                    'Accept': 'text/csv'
                }
            });

            if (!response.ok) throw new Error('Export failed');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `learner-progress-report-${new Date().toISOString()}.csv`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

        } catch (error) {
            console.error('Export error:', error);
            // Show error notification
        }
    };

    useEffect(() => {
        fetchData();
    }, [page, rowsPerPage]);

    useEffect(() => {
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
        setSearchTimeout(
            setTimeout(() => {
                setPage(0);
                fetchData();
            }, 500)
        );
    }, [filters.search]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const handleSearchChange = (event) => {
        const searchValue = event.target.value;
        setFilters(prev => ({ ...prev, search: searchValue }));
    };

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h4" gutterBottom className='page-title'>
                Learner Progress Report
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                {/* Left side - Search */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <TextField
                        placeholder="Learner"
                        className="pink-filter"
                        sx={{ height: '35px' }}
                        variant="outlined"
                        size="small"
                        value={filters.search}
                        onChange={handleSearchChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="end">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                {/* Right side - Export button */}
                <Box>
                    <Button
                        variant="outlined"
                        startIcon={<FileDownloadIcon />}
                        onClick={handleExport}
                    >
                        <b>Export Report</b>
                    </Button>
                </Box>
            </Box>

            <ResponsiveTable
                columns={columns}   
                rows={data}
                loading={loading}
                emptyTitle="No learner records found"
                renderCell={(column, row) => {
                    const value = column.format ? column.format(row) : row[column.id];

                    if (column.id === 'completedpercent') {
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 40 }}>
                            <LinearProgress
                            variant="determinate"
                            value={Number(value) || 0}
                            sx={{
                                height: 8,
                                backgroundColor: tokens.color.bgTertiary,
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: Number(value) >= 100 ? tokens.color.positive : tokens.color.brand,
                                },
                            }}
                            />
                        </Box>
                        <Typography variant="body2">
                            {value || 0}%
                        </Typography>
                        </Box>
                    );
                    }

                    return value;
                }}
                />
        </Box>
    );
};

export default LearnerProgressReport;